import { UserInformations, UserRole, EUserRole } from '@/services/connecteur'
import serviceModule from '@/store/modules/ServiceModule'
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import store from '@/store'

@Module({ namespaced: true, name: 'userModule', dynamic: true, store })
export class UserModule extends VuexModule {
  public token = ''
  public userCache?: UserInformations
  public userSurrogacies: Array<UserInformations> = []
  public realUserId?: number
  private rolesCache: UserRole[] = new Array<UserRole>()

  public get isConnectedAsAnother () {
    return this.userCache &&
      this.realUserId &&
      this.userCache.id !== this.realUserId
  }

  public get user(): UserInformations | undefined {
    return this.userCache
  }

  @Mutation
  public updateToken (token: string) {
    this.token = token
  }

  @Mutation
  public updateUser (user: UserInformations) {
    this.userCache = user
  }

  @Mutation
  private updateRoles (roles: UserRole[]) {
    this.rolesCache = roles
  }

  @Mutation
  private updateSurrogacies (surrogacies: Array<UserInformations>) {
    this.userSurrogacies = surrogacies
  }

  @Mutation
  public setRealUserId (userId: number) {
    this.realUserId = userId
  }

  @Action({ rawError: true })
  private loadRoles () {
    serviceModule.userRoleApi.getAllUserRoles().then(result => {
      this.updateRoles(result)
    })
  }

  @Action({ rawError: true })
  public loadSurrogacies () {
    serviceModule.delegationApi.getSurrogacies().then(result => {
      this.updateSurrogacies(result)
    })
  }

  public get id (): number | undefined {
    return this.userCache?.id
  }

  public get roles (): UserRole[] {
    if (this.rolesCache.length === 0) {
      this.loadRoles()
    }

    return this.rolesCache
  }

  public get userIsAdmin () {
    return this.userCache?.role === EUserRole.ADMINISTRATOR
  }

  public get userIsKAM () {
    return this.userCache?.role === EUserRole.KEY_ACCOUNT_MANAGER
  }

  public get userIsDemandPlanner () {
    return this.userCache?.role === EUserRole.DEMAND_PLANNER
  }

  public get userIsKAMAssistant () {
    return this.userCache?.role === EUserRole.KAM_ASSISTANT
  }

  public get userIsITLocal () {
    return this.userCache?.role === EUserRole.IT_LOCAL
  }

  public get userIsSNP () {
    return this.userCache?.role === EUserRole.SUPPLY_NETWORK_PLANNER
  }

  public get userIsReportingUser () {
    return this.userCache?.role === EUserRole.REPORTING
  }

  public get surrogacies () {
    return this.userSurrogacies
  }

  public get canManageUser () {
    return (
      this.userCache?.isSuperAdmin ||
      this.userIsAdmin ||
      this.userIsITLocal ||
      this.userCache?.keyUser
    )
  }

  public get canManageSnpMapping () {
    return (
      this.userIsAdmin ||
      (this.userIsSNP && this.userCache?.keyUser)
    )
  }

  public get canManageCustomer () {
    return (
      this.userCache?.isSuperAdmin ||
      this.userIsAdmin ||
      this.userIsITLocal ||
      this.userIsDemandPlanner
    )
  }

  @Action({ rawError: true })
  public isKeyUserOnSalesOrg (salesOrgId?: number) {
    let result = this.userIsAdmin
    if (!result) {
      result =
        (this.userCache?.keyUser || this.userIsITLocal) &&
        this.userCache?.userSalesOrg
          ?.map(s => s.salesOrg?.id)
          .includes(salesOrgId) || false
    }

    return result
  }

  @Action({ rawError: true })
  public isUserAdminOnSalesOrg (salesOrgId?: number): boolean {
    if (this.userIsAdmin) {
      return true
    }

    if (this.userCache?.role === EUserRole.IT_LOCAL || this.userCache?.keyUser) {
      const res = this.userCache?.userSalesOrg
        ?.map(s => s.salesOrg?.id)
        .includes(salesOrgId)
      const retour = res || false
      return !salesOrgId || retour
    }
    return false
  }

  @Action({ rawError: true })
  public isUserKamInSalesOrg (salesOrgId?: number): boolean {
    if (this.isUserAdminOnSalesOrg(salesOrgId)) {
      return true
    }
    if (
      this.userCache?.role === EUserRole.KAM_ASSISTANT ||
      this.userCache?.role === EUserRole.KEY_ACCOUNT_MANAGER
    ) {
      const res = this.userCache?.userSalesOrg
        ?.map(s => s.salesOrg?.id)
        .includes(salesOrgId)
      const retour = res || false
      return !salesOrgId || retour
    }
    return false
  }
}

export default getModule(UserModule)
